import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { useTranslation } from 'react-i18next';
import { formatTemperature } from '@/utils/temperature';

function ThermometerIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6.5a3 3 0 016 0V19a5 5 0 01-6 0z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function AlertIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function HistoryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12a9 9 0 019-9" />
    </svg>
  );
}

export default function TemperatureLogsPage() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const { t, i18n } = useTranslation();

  const [activeEquipmentId, setActiveEquipmentId] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState('');
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PAGE_SIZE = 20;

  const { data: equipmentList, isLoading: equipmentLoading } = useQuery({
    queryKey: ['equipment'],
    queryFn: async () => {
      const all = await apiClient.getEquipment();
      const TEMP_EXCLUDED = ['Friteuse', 'Four'];
      return all.filter((e: any) => !TEMP_EXCLUDED.includes(e.type));
    },
  });

  const { data: logsData, isLoading: logsLoading } = useQuery({
    queryKey: ['temperature-logs-today'],
    queryFn: () => apiClient.getTemperatureLogs(0, 500),
    refetchInterval: 30_000,
  });

  const createLogMutation = useMutation({
    mutationFn: (data: { equipmentId: string; temperatureC: number }) =>
      apiClient.createTemperatureLog(data.equipmentId, data.temperatureC, new Date().toISOString()),
    onSuccess: (response: any) => {
      if (response.incidentCreated) {
        addToast(t('temperature.recordedWithIncident'), 'warning');
        queryClient.invalidateQueries({ queryKey: ['incidents'] });
      } else {
        addToast(t('temperature.recorded'), 'success');
      }
      queryClient.invalidateQueries({ queryKey: ['temperature-logs-today'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-summary'] });
      setActiveEquipmentId(null);
      setInputValue('');
      setInputError('');
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || t('temperature.recordError');
      addToast(message, 'error');
    },
  });

  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);

  const allLogs: any[] = logsData?.data || [];
  const todayLogs = allLogs.filter((l) => new Date(l.measuredAt) >= todayStart);

  // Build equipment name lookup
  const equipmentMap: Record<string, string> = {};
  if (equipmentList) {
    for (const e of equipmentList as any[]) equipmentMap[e.id] = e.name;
  }

  // Group history logs by date (most recent first)
  const sortedLogs = [...allLogs].sort(
    (a, b) => new Date(b.measuredAt).getTime() - new Date(a.measuredAt).getTime()
  );

  const groupedByDate: { date: string; logs: any[] }[] = [];
  for (const log of sortedLogs) {
    const dateKey = new Date(log.measuredAt).toLocaleDateString(i18n.language, {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
    const existing = groupedByDate.find((g) => g.date === dateKey);
    if (existing) {
      existing.logs.push(log);
    } else {
      groupedByDate.push({ date: dateKey, logs: [log] });
    }
  }

  const pagedLogs = sortedLogs.slice(0, (historyPage + 1) * HISTORY_PAGE_SIZE);
  const hasMore = sortedLogs.length > pagedLogs.length;

  const getEquipmentStatus = (equipId: string) => {
    const logs = todayLogs.filter((l) => l.equipmentId === equipId);
    if (logs.length === 0) return { state: 'pending' as const, logs };
    const hasAlert = logs.some((l) => l.conformity === 'OUT_OF_RANGE');
    return { state: hasAlert ? 'alert' as const : 'ok' as const, logs };
  };

  const handleOpen = (equipId: string) => {
    if (activeEquipmentId === equipId) {
      setActiveEquipmentId(null);
      setInputValue('');
      setInputError('');
    } else {
      setActiveEquipmentId(equipId);
      setInputValue('');
      setInputError('');
    }
  };

  const handleSubmit = (equipId: string, _equip: any) => {
    setInputError('');
    const num = parseFloat(inputValue.replace(',', '.'));
    if (isNaN(num) || inputValue.trim() === '') {
      setInputError(t('temperature.invalidTemperature'));
      return;
    }
    if (num < -50 || num > 150) {
      setInputError(t('temperature.invalidTemperature'));
      return;
    }
    createLogMutation.mutate({ equipmentId: equipId, temperatureC: num });
  };

  const isLoading = equipmentLoading || logsLoading;

  const now = new Date();
  const dateLabel = now.toLocaleDateString(i18n.language, {
    weekday: 'long', day: 'numeric', month: 'long',
  });
  const timeLabel = now.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  const doneCount = equipmentList
    ? equipmentList.filter((e: any) => getEquipmentStatus(e.id).state !== 'pending').length
    : 0;
  const totalCount = equipmentList?.length ?? 0;

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-7xl mx-auto space-y-8">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">{t('temperature.staffTitle')}</h1>
          <p className="text-sm text-slate-500 mt-1 capitalize">{dateLabel} · {timeLabel}</p>
        </div>

        {!isLoading && totalCount > 0 && (
          <div className="sm:min-w-64 lg:min-w-80">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-sm font-medium text-slate-700">
                {doneCount}/{totalCount} {t('temperature.staffDone')}
              </span>
              {doneCount === totalCount && (
                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">
                  {t('temperature.staffAllDone')}
                </span>
              )}
            </div>
            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-500"
                style={{ width: totalCount > 0 ? `${(doneCount / totalCount) * 100}%` : '0%' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <div className="py-24 flex flex-col items-center gap-3 text-slate-400">
          <svg className="animate-spin w-8 h-8" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <span className="text-sm">{t('temperature.loadingEquipment')}</span>
        </div>
      )}

      {/* ── Empty state ── */}
      {!isLoading && equipmentList?.length === 0 && (
        <div className="py-24 text-center text-slate-500">
          <ThermometerIcon className="w-12 h-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">{t('temperature.noEquipment')}</p>
        </div>
      )}

      {/* ── Equipment grid ── */}
      {!isLoading && equipmentList && equipmentList.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {equipmentList.map((equip: any) => {
            const { state, logs } = getEquipmentStatus(equip.id);
            const lastLog = logs.length > 0 ? logs[logs.length - 1] : null;
            const isActive = activeEquipmentId === equip.id;
            const isSubmitting = createLogMutation.isPending && activeEquipmentId === equip.id;

            const borderColor =
              state === 'ok' ? 'border-t-green-500' :
              state === 'alert' ? 'border-t-red-500' :
              'border-t-slate-300';

            const bgColor =
              state === 'ok' ? 'bg-green-50/40' :
              state === 'alert' ? 'bg-red-50/40' :
              'bg-white';

            return (
              <div
                key={equip.id}
                className={`rounded-xl border border-slate-200 border-t-4 ${borderColor} ${bgColor} overflow-hidden transition-all duration-200 flex flex-col`}
              >
                {/* Card header */}
                <button
                  type="button"
                  className="w-full flex items-start gap-3 px-4 pt-4 pb-3 text-left"
                  onClick={() => handleOpen(equip.id)}
                >
                  <div className={`w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${
                    state === 'ok' ? 'bg-green-100' :
                    state === 'alert' ? 'bg-red-100' :
                    'bg-slate-100'
                  }`}>
                    {state === 'ok' && <CheckIcon className="w-4 h-4 text-green-600" />}
                    {state === 'alert' && <AlertIcon className="w-4 h-4 text-red-600" />}
                    {state === 'pending' && <ThermometerIcon className="w-4 h-4 text-slate-400" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate leading-snug">{equip.name}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {formatTemperature(equip.minTempC)} → {formatTemperature(equip.maxTempC)}
                    </p>
                    {lastLog ? (
                      <div className="flex items-center gap-1.5 mt-2">
                        <ClockIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                        <span className={`text-sm font-bold ${lastLog.conformity === 'OK' ? 'text-green-700' : 'text-red-700'}`}>
                          {lastLog.temperatureC}°C
                        </span>
                        <span className="text-xs text-slate-400">
                          · {new Date(lastLog.measuredAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {logs.length > 1 && (
                          <span className="text-xs text-slate-400">· ×{logs.length}</span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-amber-600 font-medium mt-2">{t('temperature.staffNotYet')}</p>
                    )}
                  </div>

                  <ChevronDownIcon className={`w-4 h-4 text-slate-400 flex-shrink-0 mt-1 transition-transform duration-200 ${isActive ? 'rotate-180' : ''}`} />
                </button>

                {/* Expanded: today's readings + entry form */}
                {isActive && (
                  <div className="border-t border-slate-100">
                    {/* Today's readings for this equipment */}
                    {logs.length > 0 && (
                      <div className="px-4 pt-3 pb-2">
                        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                          {t('temperature.staffTitle')}
                        </p>
                        <div className="space-y-1.5">
                          {[...logs].reverse().map((log: any) => (
                            <div key={log.id} className="flex items-center justify-between">
                              <span className="text-xs text-slate-500">
                                {new Date(log.measuredAt).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              <span className={`text-sm font-bold ${log.conformity === 'OK' ? 'text-green-700' : 'text-red-600'}`}>
                                {log.temperatureC}°C
                              </span>
                              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                                log.conformity === 'OK'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {log.conformity === 'OK' ? 'OK' : t('temperature.outOfRangeLabel')}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="border-t border-slate-100 mt-3" />
                      </div>
                    )}

                    {/* Entry form */}
                    <div className="px-4 pb-4 pt-2">
                      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                        {t('temperature.newReading')}
                      </p>
                      <div className="flex gap-2">
                        <div className="flex-1 relative">
                          <input
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            autoFocus
                            value={inputValue}
                            onChange={(e) => { setInputValue(e.target.value); setInputError(''); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(equip.id, equip); }}
                            placeholder={t('temperature.temperaturePlaceholder')}
                            className="w-full px-3 py-2.5 text-lg font-bold border-2 border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center"
                            disabled={isSubmitting}
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 font-semibold text-xs pointer-events-none">°C</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleSubmit(equip.id, equip)}
                          disabled={isSubmitting || !inputValue.trim()}
                          className="px-4 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 transition disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
                        >
                          {isSubmitting ? (
                            <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                            </svg>
                          ) : (
                            <CheckIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      {inputError && (
                        <p className="text-red-600 text-xs mt-1.5 font-medium">{inputError}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── History table ── */}
      {!isLoading && allLogs.length > 0 && (
        <div>
          <div className="flex items-center gap-2 mb-4">
            <HistoryIcon className="w-5 h-5 text-slate-400" />
            <h2 className="text-lg font-semibold text-slate-800">{t('temperature.historyTitle')}</h2>
            <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">
              {t('temperature.historyCount', { count: allLogs.length })}
            </span>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('temperature.headerDateTime')}</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('temperature.equipment')}</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('temperature.temperature')}</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('temperature.headerConformity')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {pagedLogs.map((log: any, idx: number) => {
                  const measuredDate = new Date(log.measuredAt);
                  const isToday = measuredDate >= todayStart;
                  const prevLog = pagedLogs[idx - 1];
                  const prevDate = prevLog ? new Date(prevLog.measuredAt) : null;

                  // Date separator row
                  const showDateSeparator = idx === 0 ||
                    measuredDate.toDateString() !== (prevDate?.toDateString() ?? '');

                  const dateSeparator = showDateSeparator ? (
                    <tr key={`sep-${log.id}`} className="bg-slate-50/80">
                      <td colSpan={4} className="px-4 py-2">
                        <span className="text-xs font-semibold text-slate-500 capitalize">
                          {isToday
                            ? t('temperature.today')
                            : measuredDate.toLocaleDateString(i18n.language, {
                                weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                              })}
                        </span>
                      </td>
                    </tr>
                  ) : null;

                  return (
                    <>
                      {dateSeparator}
                      <tr key={log.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-4 py-2.5 text-slate-500 tabular-nums whitespace-nowrap">
                          {measuredDate.toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="px-4 py-2.5 text-slate-700 font-medium">
                          {equipmentMap[log.equipmentId] ?? log.equipmentId}
                        </td>
                        <td className="px-4 py-2.5 text-right tabular-nums">
                          <span className={`font-bold text-base ${log.conformity === 'OK' ? 'text-green-700' : 'text-red-600'}`}>
                            {log.temperatureC}°C
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          {log.conformity === 'OK' ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                              <CheckIcon className="w-3 h-3" /> OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                              <AlertIcon className="w-3 h-3" /> {t('temperature.outOfRangeLabel')}
                            </span>
                          )}
                        </td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
            </table>

            {hasMore && (
              <div className="px-4 py-3 border-t border-slate-100 text-center">
                <button
                  type="button"
                  onClick={() => setHistoryPage((p) => p + 1)}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  {t('temperature.showMore', { count: sortedLogs.length - pagedLogs.length })}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
